// src/fileUtils/FileReferenceHelper.js
/**
 * FileReferenceHelper - Utility class for managing file references in IndexedDB
 * Handles storage and retrieval of file references, allowing users to access their
 * previously uploaded files directly from their system
 */
export default class FileReferenceHelper {
  constructor() {
    this.dbName = 'AnthropicChatFiles';
    this.storeName = 'fileReferences';
    this.db = null;
  }

  /**
   * Initialize the IndexedDB database connection
   */
  async initDatabase() {
    if (this.db) return this.db;
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id', autoIncrement: true });
          // Create indexes for common queries
          store.createIndex('path', 'path', { unique: false });
          store.createIndex('threadId', 'threadId', { unique: false });
          store.createIndex('uploadDate', 'uploadDate', { unique: false });
          store.createIndex('name', 'name', { unique: false }); // Index for file name - needed for cross-thread reference lookup
        }
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        console.log('File reference database initialized');
        resolve(this.db);
      };
      
      request.onerror = (event) => {
        console.error('Error initializing file reference database:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Store a reference to an uploaded file
   * @param {File} file - The file object from the upload
   * @param {string} threadId - ID of the thread where file was uploaded
   * @returns {Promise<number>} - ID of the stored reference
   */
  async storeFileReference(file, threadId) {
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Check if this file already exists in the store for this thread
      const index = store.index('threadId');
      const getRequest = index.getAll(threadId);
      
      getRequest.onsuccess = () => {
        const existingRefs = getRequest.result;
        const exists = existingRefs.some(ref => ref.name === file.name);
        
        if (!exists) {
          const fileRef = {
            name: file.name,
            path: file.name, // Initial path is just the filename
            type: file.type,
            size: file.size,
            threadId: threadId,
            uploadDate: new Date().toISOString(),
            lastChecked: new Date().toISOString(),
            exists: true // Assume it exists when first uploaded
          };
          
          const request = store.add(fileRef);
          
          request.onsuccess = (event) => {
            resolve(event.target.result);
          };
          
          request.onerror = (event) => {
            console.error('Error storing file reference:', event.target.error);
            reject(event.target.error);
          };
        } else {
          // File already exists, just resolve with null
          resolve(null);
        }
      };
      
      getRequest.onerror = (event) => {
        console.error('Error checking for existing files:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Get all file references for a specific thread
   * @param {string} threadId - Thread ID to fetch references for
   * @returns {Promise<Array>} - Array of file references
   */
  async getFileReferencesForThread(threadId) {
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('threadId');
      const request = index.getAll(threadId);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = (event) => {
        console.error('Error getting file references:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Find references to the same file in other threads
   * @param {string} fileName - Name of the file to find
   * @param {string} excludeThreadId - Thread ID to exclude from the search
   * @returns {Promise<Array>} - Array of references to the same file in different threads
   */
  async findReferencesByFileNameInOtherThreads(fileName, excludeThreadId) {
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const nameIndex = store.index('name');
      const request = nameIndex.getAll(fileName);
      
      request.onsuccess = () => {
        // Filter out references from the excluded thread
        const otherThreadReferences = request.result.filter(ref => 
          ref.threadId !== excludeThreadId
        );
        resolve(otherThreadReferences);
      };
      
      request.onerror = (event) => {
        console.error('Error finding references in other threads:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Cleanup file references for a deleted thread
   * @param {string} threadId - ID of the thread that was deleted
   * @returns {Promise<object>} - Summary of cleanup results
   */
  async cleanupFileReferencesForThread(threadId) {
    // Get all file references for this thread
    const threadReferences = await this.getFileReferencesForThread(threadId);
    
    if (threadReferences.length === 0) {
      return { deleted: 0, retained: 0 };
    }
    
    let deleted = 0;
    let retained = 0;
    
    // Process each reference
    for (const reference of threadReferences) {
      try {
        // Check if this file is referenced in other threads
        const otherThreadReferences = await this.findReferencesByFileNameInOtherThreads(
          reference.name, 
          threadId
        );
        
        if (otherThreadReferences.length === 0) {
          // This file is only referenced in the deleted thread, so delete the reference
          await this.deleteFileReference(reference.id);
          deleted++;
          console.log(`Deleted orphaned file reference: ${reference.name}`);
        } else {
          // The file is used in other threads, so just delete this reference
          // (since each thread has its own reference in our current schema)
          await this.deleteFileReference(reference.id);
          retained++;
          console.log(`Removed file reference from thread: ${reference.name} (used in ${otherThreadReferences.length} other threads)`);
        }
      } catch (err) {
        console.error(`Error processing file reference ${reference.name}:`, err);
      }
    }
    
    return { deleted, retained };
  }  

  /**
   * Check if a file exists without interactive prompts
   * This is a best-effort check and may not be reliable across all browsers
   * @param {Object} fileRef - File reference to check
   * @returns {Promise<boolean>} - Whether the file likely exists
   */
  async checkFileExistsNonInteractive(fileRef) {
    // Browser API checks (limited capabilities due to security restrictions)
    try {
      // For browsers that support File and Directory Entries API
      // This is mostly deprecated but may work in some browsers
      if ('FileSystem' in window && 'resolveLocalFileSystemURL' in window) {
        try {
          return new Promise((resolve) => {
            window.resolveLocalFileSystemURL(
              `file://${fileRef.path}`,
              () => resolve(true),
              () => resolve(false)
            );
          });
        } catch (err) {
          console.warn('FileSystem API check failed:', err);
        }
      }
      
      // For recently accessed files, some browsers might have a cache
      // This is mostly a placeholder as browsers don't provide reliable
      // ways to check file existence non-interactively
      
      // Fallback: use file last checked status if available
      if (fileRef.lastChecked) {
        const lastChecked = new Date(fileRef.lastChecked);
        const now = new Date();
        // If checked within the last hour, use the saved status
        if ((now - lastChecked) < 60 * 60 * 1000) {
          return !!fileRef.exists;
        }
      }
      
      // Default to true if we can't determine (optimistic approach)
      return true;
    } catch (err) {
      console.warn('File existence check error:', err);
      return true; // Default to assuming file exists
    }
  }

  /**
   * Update the existence status of a file reference
   * @param {Object} fileRef - File reference to update
   * @param {boolean} exists - Whether the file exists
   */
  async updateFileExistence(fileRef, exists) {
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Update the file reference
      fileRef.exists = exists;
      fileRef.lastChecked = new Date().toISOString();
      
      const request = store.put(fileRef);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Error updating file reference:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Update the file path for a reference
   * @param {Object} fileRef - File reference to update
   * @param {string} newPath - New file path
   */
  async updateFilePath(fileRef, newPath) {
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Update the file reference
      fileRef.path = newPath;
      fileRef.lastChecked = new Date().toISOString();
      
      const request = store.put(fileRef);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Error updating file path:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Delete a file reference
   * @param {number} id - ID of the file reference to delete
   */
  async deleteFileReference(id) {
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Error deleting file reference:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  /**
   * Clear all file references for a thread
   * @param {string} threadId - Thread ID to clear references for
   */
  async clearThreadReferences(threadId) {
    // Get all references for this thread
    const refs = await this.getFileReferencesForThread(threadId);
    
    // Ensure database is initialized
    const db = await this.initDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      let completed = 0;
      let errors = 0;
      
      // No references to delete
      if (refs.length === 0) {
        resolve();
        return;
      }
      
      // Delete each reference
      refs.forEach(ref => {
        const request = store.delete(ref.id);
        
        request.onsuccess = () => {
          completed++;
          if (completed + errors === refs.length) {
            resolve();
          }
        };
        
        request.onerror = (event) => {
          console.error('Error clearing thread references:', event.target.error);
          errors++;
          if (completed + errors === refs.length) {
            if (errors === refs.length) {
              reject(new Error('Failed to clear all thread references'));
            } else {
              resolve();
            }
          }
        };
      });
    });
  }

  /**
   * Check if a file exists at its original location
   * Uses File System Access API with fallbacks
   * 
   * @param {string} fileName - Name of the file to check
   * @returns {Promise<boolean>} - Whether the file exists
   */
  async checkFileExists(fileName) {
    // Use File System Access API if available
    if ('showOpenFilePicker' in window) {
      try {
        // Ask user to select the file
        alert(`To verify access to "${fileName}", please select it from your file system.`);
        const [fileHandle] = await window.showOpenFilePicker({
          id: 'verify-file',
          startIn: 'downloads',
        });
        
        return !!fileHandle;
      } catch (err) {
        console.warn('File System Access API check failed:', err);
        return false;
      }
    }

    // Fallback: We can't reliably check file existence in a web browser
    // Return true to allow the user to try to open it
    return true;
  }

  /**
   * Enhanced file opening that ensures better user experience
   * @param {Object} fileRef - File reference to open
   * @returns {Promise<boolean>} - Whether operation was successful
   */
  async openFileWithAssociatedApp(fileRef) {
    // Try using File System Access API (modern browsers)
    if ('showOpenFilePicker' in window) {
      try {
        // Improved UX: Better prompt and instructions
        const options = {
          id: 'open-with-app',
          startIn: 'downloads',
          // Try to filter by file type if possible
          types: this.getFilePickerTypes(fileRef),
        };
        
        // Display a clearer prompt to the user to guide them
        const promptElement = document.createElement('div');
        promptElement.style.position = 'fixed';
        promptElement.style.top = '10px';
        promptElement.style.left = '50%';
        promptElement.style.transform = 'translateX(-50%)';
        promptElement.style.backgroundColor = '#fff';
        promptElement.style.color = '#333';
        promptElement.style.padding = '15px 20px';
        promptElement.style.borderRadius = '8px';
        promptElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        promptElement.style.zIndex = '10000';
        promptElement.style.maxWidth = '400px';
        promptElement.style.textAlign = 'center';
        
        promptElement.innerHTML = `
          <h3 style="margin-top:0;margin-bottom:8px;font-size:16px;">Select "${fileRef.name}"</h3>
          <p style="margin:0;font-size:14px;">Please locate and select the file in the dialog that opens.</p>
        `;
        
        document.body.appendChild(promptElement);
        
        // Short delay to ensure user sees the message
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Let user select the file
        console.log(`Opening file: ${fileRef.name}`);
        const [fileHandle] = await window.showOpenFilePicker(options);
        
        // Remove prompt
        document.body.removeChild(promptElement);
        
        // Get file from handle
        const file = await fileHandle.getFile();
        
        // Instead of just using window.open, try to:
        // 1. Create an object URL
        const url = URL.createObjectURL(file);
        
        // 2. Determine if we should directly open or download based on file type
        if (this.canDisplayInBrowser(fileRef.type)) {
          // For viewable files (images, PDFs, text), open in new tab
          window.open(url, '_blank');
        } else {
          // For other files, trigger a download with the correct name
          const a = document.createElement('a');
          a.href = url;
          a.download = fileRef.name;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        
        // Clean up object URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        
        // Update the file reference with new path (if possible)
        try {
          // This will work in some browsers that expose the path
          // Not standardized, so wrapped in try/catch
          if (file.path) {
            await this.updateFilePath(fileRef, file.path);
          }
        } catch (pathErr) {
          console.log('Could not update file path:', pathErr);
        }
        
        // Update the file's existence status
        await this.updateFileExistence(fileRef, true);
        
        return true;
      } catch (err) {
        console.error("File System Access API error:", err);
        // Show a friendly error message
        this.showErrorMessage(`Couldn't open "${fileRef.name}". ${err.message || 'File access error.'}`);
        // Fall through to fallback
      }
    }
    
    // Fallback for older browsers - try to be more helpful
    try {
      // This will likely be blocked by most browsers, but worth a try
      window.open(`file://${fileRef.path}`, '_blank');
      return true;
    } catch (err) {
      console.error("Fallback file opening error:", err);
      // Show a more helpful error with instructions
      this.showErrorMessage(`Your browser doesn't support direct file access. Please manually locate and open: "${fileRef.name}"`);
      return false;
    }
  }

  /**
   * Determine if a file type can be displayed in browser
   * @param {string} fileType - MIME type of the file
   * @returns {boolean} - Whether browser can display this type
   */
  canDisplayInBrowser(fileType) {
    const displayableTypes = [
      'image/',          // All image types
      'application/pdf', // PDF documents
      'text/',           // All text types
      'video/',          // Videos (though may depend on codecs)
      'audio/'           // Audio files
    ];
    
    return displayableTypes.some(type => fileType.startsWith(type));
  }

  /**
   * Show a user-friendly error message
   * @param {string} message - Error message to display
   */
  showErrorMessage(message) {
    // Create styled error element
    const errorElement = document.createElement('div');
    errorElement.style.position = 'fixed';
    errorElement.style.top = '10px';
    errorElement.style.left = '50%';
    errorElement.style.transform = 'translateX(-50%)';
    errorElement.style.backgroundColor = '#fff1f0';
    errorElement.style.color = '#cf1322';
    errorElement.style.padding = '12px 16px';
    errorElement.style.borderRadius = '4px';
    errorElement.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    errorElement.style.zIndex = '10000';
    errorElement.style.maxWidth = '400px';
    
    errorElement.innerHTML = `
      <div style="display:flex;align-items:center;">
        <span style="margin-right:8px;font-size:16px;">⚠️</span>
        <span style="font-size:14px;">${message}</span>
      </div>
    `;
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.position = 'absolute';
    closeButton.style.top = '8px';
    closeButton.style.right = '8px';
    closeButton.style.background = 'none';
    closeButton.style.border = 'none';
    closeButton.style.fontSize = '16px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.color = '#cf1322';
    
    closeButton.onclick = () => document.body.removeChild(errorElement);
    
    errorElement.appendChild(closeButton);
    document.body.appendChild(errorElement);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (document.body.contains(errorElement)) {
        errorElement.style.opacity = '0';
        errorElement.style.transition = 'opacity 0.3s ease-out';
        
        setTimeout(() => {
          if (document.body.contains(errorElement)) {
            document.body.removeChild(errorElement);
          }
        }, 300);
      }
    }, 5000);
  }
  
  /**
   * Helper method to get appropriate file types for file picker
   * @param {Object} fileRef - File reference
   * @returns {Array} - Array of file types for picker
   */
  getFilePickerTypes(fileRef) {
    const fileType = fileRef.type;
    const fileName = fileRef.name;
    const extension = fileName.split('.').pop().toLowerCase();
    
    const types = [];
    
    // Add specific type based on file
    if (fileType.startsWith('image/')) {
      types.push({
        description: 'Image Files',
        accept: {
          'image/*': [`.${extension}`]
        }
      });
    } else if (fileType === 'application/pdf') {
      types.push({
        description: 'PDF Documents',
        accept: {
          'application/pdf': ['.pdf']
        }
      });
    } else if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') {
      types.push({
        description: 'Spreadsheet Files',
        accept: {
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'text/csv': ['.csv']
        }
      });
    } else {
      // Default to all files
      types.push({
        description: 'All Files',
        accept: {'*/*': ['.*']}
      });
    }
    
    return types;
  }

  /**
   * Format file size in human-readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted size
   */
  static formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Get appropriate icon for file type
   * @param {string} fileType - MIME type
   * @returns {string} - Icon character
   */
  static getFileIcon(fileType) {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') return '📊';
    if (fileType.startsWith('text/')) return '📝';
    return '📎';
  }
}